import { apiRequest } from '../../../shared/services/apiClient';
import type {
  ApiToolConnection,
  ApiToolConnectionHeader,
  ApiToolDefinition,
  ApiToolImportedOperation,
  ApiToolParameterBinding,
  ApiToolSuggestedAuth,
  CreateApiToolConnectionRequest,
  CreateApiToolRequest,
  ImportOpenApiSpecResult,
  TestApiToolConnectionRequest,
  UpdateApiToolConnectionRequest,
  UpdateApiToolRequest,
} from '../types/ApiTools';

const normalizeParameterBinding = (value: unknown): ApiToolParameterBinding => ({
  name: String((value as ApiToolParameterBinding)?.name ?? ''),
  description: String((value as ApiToolParameterBinding)?.description ?? ''),
  location: String((value as ApiToolParameterBinding)?.location ?? 'query'),
  sourceName: String((value as ApiToolParameterBinding)?.sourceName ?? ''),
  isRequired: Boolean((value as ApiToolParameterBinding)?.isRequired ?? false),
  defaultValue: String((value as ApiToolParameterBinding)?.defaultValue ?? ''),
  example: String((value as ApiToolParameterBinding)?.example ?? ''),
});

const normalizeConnectionHeader = (value: unknown): ApiToolConnectionHeader => ({
  name: String((value as ApiToolConnectionHeader)?.name ?? ''),
  value: String((value as ApiToolConnectionHeader)?.value ?? ''),
});

const normalizeImportedOperation = (value: unknown): ApiToolImportedOperation => ({
  operationId: String((value as ApiToolImportedOperation)?.operationId ?? ''),
  httpMethod: String((value as ApiToolImportedOperation)?.httpMethod ?? ''),
  pathTemplate: String((value as ApiToolImportedOperation)?.pathTemplate ?? ''),
  summary: String((value as ApiToolImportedOperation)?.summary ?? ''),
  description: String((value as ApiToolImportedOperation)?.description ?? ''),
  tags: Array.isArray((value as ApiToolImportedOperation)?.tags)
    ? (value as ApiToolImportedOperation).tags.map(String)
    : [],
  parameterBindings: Array.isArray((value as ApiToolImportedOperation)?.parameterBindings)
    ? (value as ApiToolImportedOperation).parameterBindings.map(normalizeParameterBinding)
    : [],
  suggestedKernelFunctionName: String((value as ApiToolImportedOperation)?.suggestedKernelFunctionName ?? ''),
  suggestedDisplayName: String((value as ApiToolImportedOperation)?.suggestedDisplayName ?? ''),
});

const normalizeConnection = (value: unknown): ApiToolConnection => ({
  connectionId: String((value as ApiToolConnection)?.connectionId ?? ''),
  displayName: String((value as ApiToolConnection)?.displayName ?? ''),
  description: String((value as ApiToolConnection)?.description ?? ''),
  baseUrl: String((value as ApiToolConnection)?.baseUrl ?? ''),
  specSource: String((value as ApiToolConnection)?.specSource ?? 'manual'),
  specFormat: String((value as ApiToolConnection)?.specFormat ?? 'openapi3'),
  specUrl: String((value as ApiToolConnection)?.specUrl ?? ''),
  specContentStored: Boolean((value as ApiToolConnection)?.specContentStored ?? false),
  authType: String((value as ApiToolConnection)?.authType ?? 'none'),
  authHeaderName: String((value as ApiToolConnection)?.authHeaderName ?? ''),
  authBaseUrl: String((value as ApiToolConnection)?.authBaseUrl ?? ''),
  authTokenUrl: String((value as ApiToolConnection)?.authTokenUrl ?? ''),
  authConfigured: Boolean((value as ApiToolConnection)?.authConfigured ?? false),
  oauthGrantType: String((value as ApiToolConnection)?.oauthGrantType ?? 'authorizationCode'),
  oauthAuthorizationUrl: String((value as ApiToolConnection)?.oauthAuthorizationUrl ?? ''),
  oauthTokenUrl: String((value as ApiToolConnection)?.oauthTokenUrl ?? ''),
  oauthScopes: String((value as ApiToolConnection)?.oauthScopes ?? ''),
  oauthClientId: String((value as ApiToolConnection)?.oauthClientId ?? ''),
  oauthClientSecretConfigured: Boolean((value as ApiToolConnection)?.oauthClientSecretConfigured ?? false),
  oauthConnected: Boolean((value as ApiToolConnection)?.oauthConnected ?? false),
  defaultHeaders: Array.isArray((value as ApiToolConnection)?.defaultHeaders)
    ? (value as ApiToolConnection).defaultHeaders.map(normalizeConnectionHeader)
    : [],
  importedOperations: Array.isArray((value as ApiToolConnection)?.importedOperations)
    ? (value as ApiToolConnection).importedOperations.map(normalizeImportedOperation)
    : [],
});

const normalizeTool = (value: unknown): ApiToolDefinition => ({
  toolId: String((value as ApiToolDefinition)?.toolId ?? ''),
  connectionId: String((value as ApiToolDefinition)?.connectionId ?? ''),
  operationId: String((value as ApiToolDefinition)?.operationId ?? ''),
  kernelFunctionName: String((value as ApiToolDefinition)?.kernelFunctionName ?? ''),
  displayName: String((value as ApiToolDefinition)?.displayName ?? ''),
  description: String((value as ApiToolDefinition)?.description ?? ''),
  httpMethod: String((value as ApiToolDefinition)?.httpMethod ?? ''),
  pathTemplate: String((value as ApiToolDefinition)?.pathTemplate ?? ''),
  parameterBindings: Array.isArray((value as ApiToolDefinition)?.parameterBindings)
    ? (value as ApiToolDefinition).parameterBindings.map(normalizeParameterBinding)
    : [],
  responseMode: String((value as ApiToolDefinition)?.responseMode ?? 'full'),
  responseJsonPath: String((value as ApiToolDefinition)?.responseJsonPath ?? ''),
  isPublished: Boolean((value as ApiToolDefinition)?.isPublished ?? false),
  functionId: String((value as ApiToolDefinition)?.functionId ?? ''),
});

const normalizeSuggestedAuth = (value: unknown): ApiToolSuggestedAuth | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const auth = value as ApiToolSuggestedAuth;
  const authType = String(auth.authType ?? '').trim();
  if (!authType) {
    return null;
  }

  return {
    authType,
    authHeaderName: String(auth.authHeaderName ?? ''),
    schemeName: String(auth.schemeName ?? ''),
    description: String(auth.description ?? ''),
    oauthGrantType: String(auth.oauthGrantType ?? ''),
    oauthAuthorizationUrl: String(auth.oauthAuthorizationUrl ?? ''),
    oauthTokenUrl: String(auth.oauthTokenUrl ?? ''),
    oauthScopes: String(auth.oauthScopes ?? ''),
  };
};

const normalizeImportResult = (value: unknown): ImportOpenApiSpecResult => ({
  baseUrl: String((value as ImportOpenApiSpecResult)?.baseUrl ?? ''),
  specFormat: String((value as ImportOpenApiSpecResult)?.specFormat ?? ''),
  operationCount: Number((value as ImportOpenApiSpecResult)?.operationCount ?? 0),
  operations: Array.isArray((value as ImportOpenApiSpecResult)?.operations)
    ? (value as ImportOpenApiSpecResult).operations.map(normalizeImportedOperation)
    : [],
  warnings: Array.isArray((value as ImportOpenApiSpecResult)?.warnings)
    ? (value as ImportOpenApiSpecResult).warnings.map(String)
    : [],
  suggestedAuth: normalizeSuggestedAuth((value as ImportOpenApiSpecResult)?.suggestedAuth),
});

export const listApiToolConnections = async (): Promise<ApiToolConnection[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/api-tools/connections', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeConnection) : [];
};

export const createApiToolConnection = async (
  request: CreateApiToolConnectionRequest,
): Promise<ApiToolConnection> => {
  const result = await apiRequest<unknown>('/api/v1/ai/api-tools/connections', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return normalizeConnection(result);
};

export const updateApiToolConnection = async (
  connectionId: string,
  request: UpdateApiToolConnectionRequest,
): Promise<ApiToolConnection> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  );
  return normalizeConnection(result);
};

export const deleteApiToolConnection = async (connectionId: string): Promise<void> => {
  await apiRequest(
    `/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}`,
    { method: 'DELETE' },
  );
};

export const testApiToolConnection = async (
  connectionId: string,
  request: TestApiToolConnectionRequest = {},
): Promise<string> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/test`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  );
  return JSON.stringify(result, null, 2);
};

export const previewImportOpenApiSpec = async (
  specContent: string,
  baseUrlOverride?: string,
): Promise<ImportOpenApiSpecResult> => {
  const result = await apiRequest<unknown>('/api/v1/ai/api-tools/import-spec/preview', {
    method: 'POST',
    body: JSON.stringify({ specContent, baseUrlOverride: baseUrlOverride || undefined }),
  });
  return normalizeImportResult(result);
};

export const previewImportOpenApiSpecFromUrl = async (
  specUrl: string,
  baseUrlOverride?: string,
): Promise<ImportOpenApiSpecResult> => {
  const result = await apiRequest<unknown>('/api/v1/ai/api-tools/import-spec/preview-from-url', {
    method: 'POST',
    body: JSON.stringify({ specUrl, baseUrlOverride: baseUrlOverride || undefined }),
  });
  return normalizeImportResult(result);
};

export const previewImportOpenApiSpecUpload = async (
  file: File,
  baseUrlOverride?: string,
): Promise<ImportOpenApiSpecResult> => {
  const formData = new FormData();
  formData.append('file', file);
  if (baseUrlOverride?.trim()) {
    formData.append('baseUrlOverride', baseUrlOverride.trim());
  }

  const result = await apiRequest<unknown>('/api/v1/ai/api-tools/import-spec/preview-upload', {
    method: 'POST',
    body: formData,
  });
  return normalizeImportResult(result);
};

export const importSpecToConnection = async (
  connectionId: string,
  specContent: string,
  baseUrlOverride?: string,
): Promise<{ connection: ApiToolConnection; import: ImportOpenApiSpecResult }> => {
  const result = await apiRequest<{
    connection?: unknown;
    import?: unknown;
  }>(`/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/import-spec`, {
    method: 'POST',
    body: JSON.stringify({
      specContent,
      baseUrlOverride: baseUrlOverride || undefined,
      persistSpecContent: true,
    }),
  });

  return {
    connection: normalizeConnection(result?.connection),
    import: normalizeImportResult(result?.import),
  };
};

export const importSpecFromUrlToConnection = async (
  connectionId: string,
  specUrl: string,
  baseUrlOverride?: string,
): Promise<{ connection: ApiToolConnection; import: ImportOpenApiSpecResult }> => {
  const result = await apiRequest<{
    connection?: unknown;
    import?: unknown;
  }>(`/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/import-spec-from-url`, {
    method: 'POST',
    body: JSON.stringify({
      specUrl,
      baseUrlOverride: baseUrlOverride || undefined,
      persistSpecContent: true,
    }),
  });

  return {
    connection: normalizeConnection(result?.connection),
    import: normalizeImportResult(result?.import),
  };
};

export const importSpecUploadToConnection = async (
  connectionId: string,
  file: File,
  baseUrlOverride?: string,
): Promise<{ connection: ApiToolConnection; import: ImportOpenApiSpecResult }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (baseUrlOverride?.trim()) {
    formData.append('baseUrlOverride', baseUrlOverride.trim());
  }
  formData.append('persistSpecContent', 'true');

  const result = await apiRequest<{
    connection?: unknown;
    import?: unknown;
  }>(`/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/import-spec-upload`, {
    method: 'POST',
    body: formData,
  });

  return {
    connection: normalizeConnection(result?.connection),
    import: normalizeImportResult(result?.import),
  };
};

export const startApiToolOAuth = async (connectionId: string): Promise<string> => {
  const result = await apiRequest<{ authorizeUrl?: string }>(
    `/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/oauth/start`,
    { method: 'POST' },
  );
  return String(result?.authorizeUrl ?? '');
};

export const disconnectApiToolOAuth = async (connectionId: string): Promise<void> => {
  await apiRequest(
    `/api/v1/ai/api-tools/connections/${encodeURIComponent(connectionId)}/oauth`,
    { method: 'DELETE' },
  );
};

export const listApiTools = async (connectionId?: string): Promise<ApiToolDefinition[]> => {
  const query = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : '';
  const result = await apiRequest<unknown[]>(`/api/v1/ai/api-tools${query}`, { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeTool) : [];
};

export const createApiTool = async (request: CreateApiToolRequest): Promise<ApiToolDefinition> => {
  const result = await apiRequest<unknown>('/api/v1/ai/api-tools', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return normalizeTool(result);
};

export const updateApiTool = async (
  toolId: string,
  request: UpdateApiToolRequest,
): Promise<ApiToolDefinition> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/api-tools/${encodeURIComponent(toolId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  );
  return normalizeTool(result);
};

export const deleteApiTool = async (toolId: string): Promise<void> => {
  await apiRequest(`/api/v1/ai/api-tools/${encodeURIComponent(toolId)}`, { method: 'DELETE' });
};

export const previewApiTool = async (
  toolId: string,
  argumentsMap: Record<string, string>,
): Promise<string> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/api-tools/${encodeURIComponent(toolId)}/preview`,
    {
      method: 'POST',
      body: JSON.stringify({ arguments: argumentsMap }),
    },
  );
  return JSON.stringify(result, null, 2);
};
